import React from "react";
import { cn } from "../../DashboardPage/lib/utils";

interface ScoreRingProps {
  /** 0–100. Values outside the range are clamped. */
  value: number;
  /** Accessible description, e.g. "Popularity". */
  label?: string;
  className?: string;
}

const SIZE = 36;
const STROKE = 3;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * Compact circular score indicator: the value sits inside its own gauge rather
 * than beside a horizontal bar, so a score column costs one small square
 * instead of a number plus a wide track.
 */
export default function ScoreRing({ value, label = "Score", className }: ScoreRingProps) {
  const pct = Math.min(Math.max(value, 0), 100);
  // Start the arc at 12 o'clock and run clockwise.
  const offset = CIRCUMFERENCE * (1 - pct / 100);

  return (
    <span
      className={cn("relative inline-flex items-center justify-center shrink-0", className)}
      style={{ width: SIZE, height: SIZE }}
      role="img"
      aria-label={`${label}: ${pct} out of 100`}
    >
      <svg width={SIZE} height={SIZE} className="-rotate-90" aria-hidden="true">
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE}
          className="stroke-neutral-200"
        />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          className="stroke-primary-700"
        />
      </svg>
      <span className="absolute text-xs font-semibold tabular-nums text-black">
        {pct}
      </span>
    </span>
  );
}
